import type { CenterProps, MotionPropsFor } from '@hover/blueprint';
import { Center, motion, useTheme } from '@hover/blueprint';
import React, { useRef } from 'react';

import type { SelectorProps } from 'src/shared/components/Selector/Selector';
import {
  Selector,
  SelectorItem,
} from 'src/shared/components/Selector/Selector';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { useMaxWidthQuery } from 'src/shared/hooks/scrollHooks/useMaxWidthQuery';

import { useStickyDepth } from '../../hooks';
import { desktopNavbarHeight, navbarGlass } from '../Navbar';
import { formatSpaceType } from '../Utils';

interface SpaceTypeSelectorProps
  extends MotionPropsFor<CenterProps>,
    Pick<
      SelectorProps,
      | 'isLoading'
      | 'placeholderCount'
      | 'overflowDistanceX'
      | 'overflowDistanceY'
    > {
  currentType: string;
  onTypeChange: (type: string) => void;
  types: string[];
}

const MotionCenter = motion(Center);

const SpaceTypeSelector = ({
  currentType,
  isLoading,
  overflowDistanceX,
  overflowDistanceY,
  onTypeChange,
  placeholderCount,
  types,
  sx,
  ...props
}: SpaceTypeSelectorProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const offsetElementRef = useRef<HTMLDivElement>(null);
  const belowTablet = useMaxWidthQuery('tablet');
  const zIndex = useStickyDepth();
  const { space } = useTheme();

  return (
    <MotionCenter
      {...navbarGlass}
      display="block"
      mt={{ tablet: 200 }}
      paddingBottom={{ tablet: 200 }}
      position="sticky"
      ref={offsetElementRef}
      sx={{
        ...belowTablet({
          _before: { boxShadow: 'none !important' },
          borderRadius: '0px !important',
        }),
        ...sx,
      }}
      top={{
        base: 0,
        tablet: `calc(${desktopNavbarHeight}px + ${space[300]})`,
      }}
      zIndex={zIndex}
      {...props}
    >
      <Selector
        borderRadius="inherit"
        height={350}
        isLoading={isLoading}
        justifyContent={{ base: 'start', tablet: 'center' }}
        onChange={onTypeChange}
        overflowDistanceX={overflowDistanceX}
        overflowDistanceY={overflowDistanceY}
        placeholderCount={placeholderCount}
        py={300}
        value={currentType}
      >
        {['All', ...types].map(type => (
          <SelectorItem height="100%" key={type} value={type}>
            {type === 'All' ? t('favorites.all') : formatSpaceType(type)}
          </SelectorItem>
        ))}
      </Selector>
    </MotionCenter>
  );
};

export { SpaceTypeSelector };
